import {
    ButtonSizes,
    ButtonStyles,
    Icon,
    Icons,
    Label,
    List,
    ListItem,
    MenuButton,
    Search
} from '@silverfort/sf-components';
import { FC, useCallback, useMemo, useState } from 'react';
import ProtocolPicker from '../../../../privilegeAccounts/components/ProtocolPicker';
import { SERVICE_ACCOUNTS_PROTOCOLS_OPTIONS as PROTOCOLS } from '@silverfort-nodelibs/common/utils/consts';

const DEFAULT_PROTOCOLS = Object.values(PROTOCOLS);

interface ProtocolsPickerTabProps {
    protocols: string[];
    updateProtocols: (protocols: string[]) => void;
    disabled: boolean;
}

const ProtocolsPickerTab: FC<ProtocolsPickerTabProps> = ({ protocols, updateProtocols, disabled }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [openManualSelection, setOpenManualSelection] = useState(false);

    const closeModals = () => {
        setOpenManualSelection(false);
    };

    const listItemProtocols = useMemo(
        () => protocols.map((p) => DEFAULT_PROTOCOLS.find((protocol) => protocol.value === p)),
        [protocols]
    );

    const filteredProtocols = useMemo(
        () => listItemProtocols.filter((p) => p?.label.toLowerCase().includes(searchTerm.toLowerCase())),
        [listItemProtocols, searchTerm]
    );

    const handleProtocolRemoval = useCallback(
        (protocol) => {
            updateProtocols(protocols.filter((entity) => entity !== protocol.value));
        },
        [protocols, updateProtocols]
    );

    return (
        <div className="border border-sf-gray-100 rounded-lg w-full h-[288px]">
            <div className="py-2 pr-4 pl-6 border-b h-14 w-full flex items-center justify-between">
                <Label text={`Selected protocols (${protocols.length})`} />
                <div className="flex gap-2 items-center">
                    <Search border onChange={setSearchTerm} />
                    <MenuButton
                        disabled={disabled}
                        openByDefault={false}
                        buttonSize={ButtonSizes.Medium}
                        icon={Icons.Add}
                        iconClassName={`w-fit`}
                        className="right-0"
                        text="Add"
                        holdPressed={openManualSelection}
                        buttonStyle={ButtonStyles.Primary}
                        content={
                            <List className="p-[0px]">
                                <ListItem
                                    title="Add manually"
                                    IconLeft={() => <Icon icon={Icons.UserCheck} className="text-sf-gray-400" />}
                                    onClick={() => {
                                        setOpenManualSelection(true);
                                    }}
                                />
                            </List>
                        }
                    />
                </div>
            </div>
            {openManualSelection && (
                <div className="relative left-14">
                    <ProtocolPicker
                        selectedProtocols={listItemProtocols}
                        onSave={(changedProtocols: { label: string; value: string }[]) => {
                            updateProtocols(changedProtocols.map((p) => p.value));
                            closeModals();
                        }}
                        onClose={closeModals}
                    />
                </div>
            )}
            {!!filteredProtocols?.length ? (
                <div className="h-[230px] w-full overflow-auto">
                    <List>
                        {filteredProtocols.map((item, i) => (
                            <ListItem
                                className="cursor-default"
                                title={item?.label || ''}
                                onRemove={!disabled ? () => handleProtocolRemoval(item) : undefined}
                            />
                        ))}
                    </List>
                </div>
            ) : (
                <div className="flex flex-col text-center h-[230px] w-full items-center justify-center">
                    <div className="h-11 flex flex-col justify-between">
                        <div className="font-medium text-16 text-sf-gray-800">
                            {searchTerm ? 'No Results' : `No protocols yet`}
                        </div>
                        <div className="text-13 text-sf-gray-500">
                            {searchTerm ? 'Try refining your search' : ` Add items by clicking the “Add” button`}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProtocolsPickerTab;
